import * as React from "react"
import { Plus, Search } from "lucide-react"
import { GroupCard } from "./group-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { EmptyState } from "@/components/ui/empty-state"
import { GroupCardSkeleton } from "@/components/ui/loading-state"
import type { Group, User } from "@/types/domain.types"

interface GroupListProps {
  groups: Group[]
  currentUser: User
  selectedGroupId?: string
  isLoading?: boolean
  isError?: boolean
  onGroupSelect?: (group: Group) => void
  onGroupEdit?: (group: Group) => void
  onGroupInvite?: (group: Group) => void
  onGroupSettings?: (group: Group) => void
  onCreateGroup?: () => void
  searchable?: boolean
}

export const GroupList: React.FC<GroupListProps> = ({
  groups,
  currentUser,
  selectedGroupId,
  isLoading,
  isError,
  onGroupSelect,
  onGroupEdit,
  onGroupInvite,
  onGroupSettings,
  onCreateGroup,
  searchable = true,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("")

  const filteredGroups = React.useMemo(() => {
    if (!searchQuery) return groups

    return groups.filter((group) =>
      group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      group.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [groups, searchQuery])

  if (isLoading) {
    return (
      <div className="space-y-4">
        {searchable && (
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search groups..."
                className="pl-10"
                disabled
              />
            </div>
            <Button disabled>
              <Plus className="mr-2 h-4 w-4" />
              New Group
            </Button>
          </div>
        )}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <GroupCardSkeleton key={i} />
          ))}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <EmptyState
        title="Failed to load groups"
        description="Something went wrong while loading your groups. Please try again."
        action={{
          label: "Retry",
          onClick: () => window.location.reload(),
        }}
      />
    )
  }

  return (
    <div className="space-y-4">
      {/* Search and Actions */}
      {(searchable || onCreateGroup) && (
        <div className="flex items-center gap-2">
          {searchable && (
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search groups..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          )}
          {onCreateGroup && (
            <Button onClick={onCreateGroup}>
              <Plus className="mr-2 h-4 w-4" />
              New Group
            </Button>
          )}
        </div>
      )}

      {/* Groups Grid */}
      {filteredGroups.length === 0 ? (
        <EmptyState
          icon={<Plus className="h-6 w-6" />}
          title={groups.length === 0 ? "No groups yet" : "No groups found"}
          description={
            groups.length === 0
              ? "Create your first group to start tracking shared expenses with friends and family."
              : searchQuery
                ? `No groups match "${searchQuery}". Try a different search term.`
                : "You don't have any groups yet."
          }
          action={
            onCreateGroup && groups.length === 0
              ? {
                  label: "Create Your First Group",
                  onClick: onCreateGroup,
                }
              : undefined
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredGroups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              currentUser={currentUser}
              isSelected={selectedGroupId === group.id}
              onSelect={onGroupSelect}
              onEdit={onGroupEdit}
              onInvite={onGroupInvite}
              onSettings={onGroupSettings}
            />
          ))}
        </div>
      )}

      {/* Search Results Info */}
      {searchQuery && filteredGroups.length > 0 && (
        <div className="text-sm text-muted-foreground text-center">
          Found {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''} matching "{searchQuery}"
        </div>
      )}
    </div>
  )
}